import { useEffect, useRef, ReactNode } from 'react';

export interface WizardLayoutProps {
  /** Current step number (1-indexed) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Callback when Continue button is clicked */
  onNext: () => void;
  /** Callback when Back button is clicked */
  onBack: () => void;
  /** Callback when Cancel button is clicked */
  onCancel: () => void;
  /** Whether the current step can proceed (enables/disables Continue button) */
  canProceed: boolean;
  /** Step-specific content to render */
  children: ReactNode;
}

/**
 * WizardLayout - Reusable wizard infrastructure component
 * 
 * Provides:
 * - Step indicator showing current step (e.g., "Step 2 of 4")
 * - Progress visualization (progress bar)
 * - Navigation buttons: Back, Continue, Cancel
 * - Step validation via canProceed prop
 * - Responsive design (desktop ≥1024px, tablet 768-1023px, mobile <768px)
 * - Accessibility features (ARIA labels, keyboard navigation, focus management)
 * 
 * Usage:
 * ```tsx
 * <WizardLayout
 *   currentStep={2}
 *   totalSteps={4}
 *   onNext={() => navigate(`/buy/${productId}/step-3`)}
 *   onBack={() => navigate(`/buy/${productId}/step-1`)}
 *   onCancel={() => navigate(-1)}
 *   canProceed={allDocumentsApproved}
 * >
 *   <StepContent />
 * </WizardLayout>
 * ```
 */
export const WizardLayout = ({
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onCancel,
  canProceed,
  children,
}: WizardLayoutProps) => {
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Focus management: focus Continue button when step loads
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      continueButtonRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStep]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Enter key activates Continue button if enabled
      if (e.key === 'Enter' && canProceed && e.target === document.body) {
        e.preventDefault();
        onNext();
      }
      // Escape key triggers Cancel
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canProceed, onNext, onCancel]);

  const isLastStep = currentStep === totalSteps;

  // Step names for progress tracker
  const stepNames = [
    'Review documents',
    'Review terms',
    'User verification',
    'Confirmation',
  ];

  return (
    <div className="flex-1 flex h-full -mx-6 -my-8 gap-6 pr-6">
      {/* Main Content Area (Left/Center) */}
      <div className="flex-1 flex flex-col p-6 lg:p-8 min-h-0 overflow-auto">
        {/* Step Indicator */}
        <div className="mb-6">
          <p className="text-sm text-text-secondary">Step {currentStep} of {totalSteps}</p>
        </div>
        
        {/* Step Content */}
        <div className="flex-1 mb-6 min-h-0">
          {children}
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-row gap-4 justify-between items-center pt-4 border-t border-gray-200">
          {/* Left side: Return */}
          <button
            type="button"
            onClick={onBack}
            className="h-11 min-h-[44px] px-6 rounded-md font-medium bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
            aria-label="Go to previous step"
          >
            Return
          </button>

          {/* Right side: Continue */}
          <button
            ref={continueButtonRef}
            type="button"
            onClick={onNext}
            disabled={!canProceed}
            className={`
              h-11 min-h-[44px] px-6 rounded-md font-medium
              transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
              ${canProceed
                ? 'bg-primary text-white hover:bg-primary-dark focus:ring-primary'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }
            `}
            aria-label={canProceed ? (isLastStep ? 'Complete wizard' : 'Continue to next step') : 'Continue button disabled (step validation required)'}
          >
            {isLastStep ? 'Complete' : 'Continue'}
          </button>
        </div>
      </div>

      {/* Progress Tracker Card (Right) - Aligned with first document card top border */}
      <div className="w-80 bg-background border border-border rounded-lg shadow-lg p-6 flex flex-col flex-shrink-0 overflow-visible mt-[118px] lg:mt-[126px] h-fit">
        {/* Progress Tracker Title */}
        <h2 className="text-lg font-bold text-text-primary mb-4 text-center">Your progress</h2>

        {/* Progress Bar */}
        <div className="mb-6" role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={totalSteps} aria-label={`Progress: ${Math.round(progressPercentage)}%`}>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-sm text-text-secondary text-center">{Math.round(progressPercentage)}%</p>
          {/* Progress percentage text for screen readers */}
          <span className="sr-only">{Math.round(progressPercentage)}% complete</span>
        </div>

        {/* Step List */}
        <div className="flex flex-col">
          {Array.from({ length: totalSteps }, (_, index) => {
            const stepNumber = index + 1;
            const isCompleted = stepNumber < currentStep;
            const isCurrent = stepNumber === currentStep;
            const stepName = stepNames[stepNumber - 1] || `Step ${stepNumber}`;
            
            return (
              <div
                key={stepNumber}
                className="flex items-center gap-3 min-h-[2rem]"
                style={{ marginBottom: index < totalSteps - 1 ? '1rem' : '0' }}
              >
                {/* Step Icon */}
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                    transition-all duration-300
                    ${isCompleted 
                      ? 'bg-primary' 
                      : isCurrent 
                        ? 'bg-white border-2 border-primary' 
                        : 'bg-gray-200'
                    }
                  `}
                  aria-hidden="true"
                >
                  {isCurrent ? (
                    <div className="flex items-center justify-center gap-0.5">
                      <span className="w-1 h-1 rounded-full bg-primary"></span>
                      <span className="w-1 h-1 rounded-full bg-primary"></span>
                      <span className="w-1 h-1 rounded-full bg-primary"></span>
                    </div>
                  ) : isCompleted ? (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                  )}
                </div>
                
                {/* Step Name */}
                <p
                  className={`
                    text-sm leading-tight transition-colors
                    ${isCurrent 
                      ? 'font-bold text-text-primary' 
                      : 'text-text-secondary'
                    }
                  `}
                >
                  {stepName}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

